import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "../../components/ui/EmptyState";
import { Button } from "../../components/ui/Button";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { ErrorText } from "../../components/ui/feedback";
import { LabelGrid } from "../../components/ui/LabelGrid";
import { useI18n } from "../../i18n/I18nProvider";
import { apiRequest } from "../../lib/api";
import type { ApiKey } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { ConfigHeader } from "../configs/ConfigHeader";
import { configurationQuery } from "../configs/queries";
import { ApiKeyRow } from "./ApiKeyRow";
import { CreateKeyModal } from "./CreateKeyModal";

export interface ApiKeysPageProps {
  workspaceId: string;
  configurationId: string;
}

const apiKeysQuery = (workspaceId: string, configurationId: string) => ({
  queryKey: ["api-keys", workspaceId, configurationId] as const,
  queryFn: () => apiRequest<ApiKey[]>(paths.apiKeys(workspaceId, configurationId)),
});

/**
 * API keys for one configuration. The sync API ships later in the desktop app, so the keys
 * are for future use. A new key is shown once, and only the hash is stored server-side.
 */
export function ApiKeysPage({ workspaceId, configurationId }: ApiKeysPageProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const configuration = useQuery(configurationQuery(workspaceId, configurationId));
  const keys = useQuery(apiKeysQuery(workspaceId, configurationId));
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["api-keys", workspaceId, configurationId] });

  const revoke = useMutation({
    mutationFn: (keyId: string) =>
      apiRequest<unknown>(paths.apiKey(workspaceId, configurationId, keyId), { method: "DELETE" }),
    onSuccess: () => {
      setRevoking(null);
      return refresh();
    },
  });

  const failed = configuration.error ?? keys.error;
  if (failed) return <ErrorText error={failed} />;
  if (!configuration.data) return <p className="text-note text-ink-2">{t("app.loading")}</p>;

  const all = keys.data ?? [];
  const active = all.filter((key) => key.revoked_at === null);
  const revoked = all.filter((key) => key.revoked_at !== null);

  return (
    <div className="space-y-6">
      <ConfigHeader workspaceId={workspaceId} configuration={configuration.data} tab="keys" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-prose text-note text-ink-2">{t("keys.note")}</p>
        <Button variant="primary" onClick={() => setCreating(true)}>
          {t("keys.create")}
        </Button>
      </div>
      <ErrorText error={revoke.error} />

      {keys.data && all.length === 0 ? <EmptyState>{t("keys.empty")}</EmptyState> : null}

      {active.length > 0 ? (
        <LabelGrid>
          {active.map((key) => (
            <ApiKeyRow
              key={key.id}
              apiKey={key}
              busy={revoke.isPending}
              onRevoke={() => setRevoking(key)}
            />
          ))}
        </LabelGrid>
      ) : null}

      {revoked.length > 0 ? (
        <details className="space-y-3">
          <summary className="inline-flex min-h-11 cursor-pointer items-center text-note font-semibold text-blue hover:underline">
            {t("keys.revokedGroup", { count: String(revoked.length) })}
          </summary>
          <LabelGrid>
            {revoked.map((key) => (
              <ApiKeyRow key={key.id} apiKey={key} busy={revoke.isPending} onRevoke={() => {}} />
            ))}
          </LabelGrid>
        </details>
      ) : null}

      {creating ? (
        <CreateKeyModal
          workspaceId={workspaceId}
          configurationId={configurationId}
          onClose={() => {
            setCreating(false);
            void refresh();
          }}
        />
      ) : null}

      {revoking ? (
        <ConfirmDialog
          title={t("keys.revokeTitle")}
          message={t("keys.confirmRevoke", { name: revoking.label })}
          confirmLabel={t("keys.revoke")}
          variant="danger"
          busy={revoke.isPending}
          error={revoke.error}
          onCancel={() => setRevoking(null)}
          onConfirm={() => revoke.mutate(revoking.id)}
        />
      ) : null}
    </div>
  );
}
